import { useToast } from '@catering/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';

import { mapSystemParametersError } from '../system-parameters-api-errors';
import { AUTO_CLOSE_KEY, isAutoCloseEnabled } from '../auto-close-setting';
import { updateSetting, type SettingListItem } from '../system-parameters.api';

interface AutoCloseSwitchProps {
  settings: readonly SettingListItem[];
  canManage: boolean;
}

/**
 * `order-cutoff-close/spec.md` → "Cierre automático": the on/off switch is saved
 * as soon as it is toggled (its own `PATCH`), apart from the form's "Guardar".
 */
export function AutoCloseSwitch({ settings, canManage }: AutoCloseSwitchProps): JSX.Element {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const enabled = isAutoCloseEnabled(settings);

  const mutation = useMutation({
    mutationFn: (next: boolean) => updateSetting(AUTO_CLOSE_KEY, next),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings'] }),
    onError: (error) => toast('error', mapSystemParametersError(error)),
  });

  return (
    <div className="flex items-start gap-3">
      <button
        type="button"
        role="switch"
        id="auto-close-switch"
        aria-checked={enabled}
        aria-labelledby="auto-close-switch-label"
        aria-describedby="auto-close-switch-help"
        disabled={!canManage || mutation.isPending}
        onClick={() => mutation.mutate(!enabled)}
        className={`relative mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${
          enabled ? 'bg-primary-600' : 'bg-slate-300'
        }`}
      >
        <span
          aria-hidden="true"
          className={`inline-block size-5 rounded-full bg-white shadow transition-transform ${
            enabled ? 'translate-x-5' : 'translate-x-0.5'
          }`}
        />
      </button>

      <div className="flex flex-col gap-1">
        <span id="auto-close-switch-label" className="text-sm font-medium text-slate-900">
          Cierre automático de pedidos
        </span>
        <span id="auto-close-switch-help" className="text-xs text-slate-600">
          {'Cada pedido se cierra solo a su hora de corte: se confirman los enviados y se cancelan los borradores. El botón "Cerrar corte" sigue disponible.'}
        </span>
      </div>
    </div>
  );
}
